import React, { useState, useMemo } from "react";
import { Modal, Form } from "react-bootstrap";
import Select from 'react-select';
import { dataUser } from "../../services/dashboard/GetUsers";
import { createUser, NewUserRequest } from "../../services/dashboard/NewUser"; // Importa el servicio
import "../../styles/dash/CreateOrEditChatBot.css";
import LoadingSpinner from "../utility/LoadingSpinner";

interface Props {
    roles: any[];
    onClose: () => void;
    onSuccess: (newUser: dataUser) => void;
    fetch: () => void;
}

interface SelectOption {
    value: number;
    label: string;
}

const CreateUserModal: React.FC<Props> = ({ roles, onClose, fetch }) => {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [status, setStatus] = useState(true);
    const [selectedRoles, setSelectedRoles] = useState<SelectOption[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [animateOut, setAnimateOut] = useState(false);



    const roleOptions = useMemo(() => {
        return roles.map(role => ({
            value: role.id,
            label: role.roleName
        }));
    }, [roles]);

    const handleClose = () => {
        setAnimateOut(true);
        setTimeout(() => {
            onClose();
            setAnimateOut(false);
        }, 300);
    };

    const validateForm = () => {
        // Validación actualizada a 8 caracteres mínimo
        if (!username.trim()) {
            setErrorMessage("El nombre de usuario es requerido");
            return false;
        }

        if (!email.trim()) {
            setErrorMessage("El email es requerido");
            return false;
        }

        if (!password) {
            setErrorMessage("La contraseña es requerida");
            return false;
        }

        if (password.length < 8) {
            setErrorMessage("La contraseña debe tener al menos 8 caracteres");
            return false;
        }

        if (password !== confirmPassword) {
            setErrorMessage("Las contraseñas no coinciden");
            return false;
        }

        if (selectedRoles.length === 0) {
            setErrorMessage("Debe asignar al menos un rol");
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        setLoading(true);
        setErrorMessage("");

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        try {
            // Preparar datos para la API
            const userData: NewUserRequest = {
                UserName: username,
                Email: email,
                Password: password,
                Status: status ? 1 : 0,
                Roles: selectedRoles.map(role => role.value)
            };

            // Llamar al servicio de creación
            const result = await createUser(userData);
            console.log(result)

            // Construir el nuevo usuario para actualizar la UI
            fetch();
            handleClose();
        } catch (error: any) {
            // Manejar errores específicos
            if (error.message.includes("expiró") || error.message.includes("Sesión")) {
                // Manejar token expirado
                setErrorMessage("Tu sesión ha expirado. Por favor, vuelve a iniciar sesión");
            } else if (error.message.includes("registrado")) {
                setErrorMessage("El correo electrónico ya está registrado");
            } else {
                setErrorMessage(error.message || "Error al crear el usuario");
            }

            console.error("Error al crear usuario:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={!animateOut}
            onHide={handleClose}
            centered
            size="lg"
            backdrop="static"
            contentClassName={`chatbot-form-modal ${animateOut ? 'modal-exit' : 'modal-enter'}`}
        >
            <Modal.Header closeButton closeVariant="white" className="border-0" />

            <Modal.Body className="chatbot-form-content">
                <h5 className="form-title text-center mb-4">
                    Crear Nuevo Usuario
                </h5>

                <Form>
                    {loading && <LoadingSpinner />}
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Nombre de usuario *</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Ej: juanperez"
                            />
                        </div>

                        <div className="form-group">
                            <label>Email *</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Ej: usuario@ejemplo.com"
                            />
                        </div>

                        <div className="form-group">
                            <label>Contraseña *</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Mínimo 8 caracteres"
                            />
                        </div>

                        <div className="form-group">
                            <label>Confirmar contraseña *</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repita la contraseña"
                            />
                        </div>

                        <div className="form-group stadoUsuario">
                            <label>¿Activo?</label>
                            <div className="status-toggle">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={status}
                                        onChange={(e) => setStatus(e.target.checked)}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>

                        <div className="form-group full-width">
                            <label>Roles *</label>
                            <Select
                                isMulti
                                options={roleOptions}
                                value={selectedRoles}
                                onChange={(newValue) => setSelectedRoles([...newValue])}
                                placeholder="Seleccione roles..."
                                className="basic-multi-select"
                                classNamePrefix="select"
                                menuPortalTarget={document.body} 
                                menuPosition="fixed" 
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 }), 
                                    control: (base) => ({
                                        ...base,
                                        border: '1px solid #444444',
                                        borderRadius: '4px',
                                        minHeight: '38px',
                                        backgroundColor: '#2a2a2a',
                                        color: '#fff'
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        backgroundColor: '#2a2a2a',
                                        color: '#fff'
                                    }),
                                    multiValue: (base) => ({
                                        ...base,
                                        backgroundColor: '#2a2a2a',
                                        color: '#fff'
                                    }),
                                    multiValueLabel: (base) => ({
                                        ...base,
                                        color: '#fff'
                                    }),
                                    option: (base, { isFocused }) => ({
                                        ...base,
                                        backgroundColor: isFocused ? '#2a2a2a' : '#1d1c1cff',
                                        color: '#fff'
                                    }),
                                    singleValue: (base) => ({
                                        ...base,
                                        color: '#fff'
                                    })
                                }}
                            />
                        </div>

                        <div className="form-group full-width">
                            {errorMessage && <div className="error-message">{errorMessage}</div>}

                            <button
                                type="button"
                                className="save-button"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "CREANDO..." : "CREAR USUARIO"}
                            </button>
                        </div>

                        <div className="form-group full-width text-center">
                            <button type="button" className="cancel-button" onClick={handleClose}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CreateUserModal;